import React, { useState, useEffect, createContext, useContext } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Search, Menu, X } from "lucide-react";
import { SITE_META } from "../mock/mockData";

const CartContext = createContext(null);

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const stored = window.localStorage.getItem("lashdollz_cart");
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("lashdollz_cart", JSON.stringify(items));
    } catch (e) {
      if (process.env.NODE_ENV === "development") {
        console.error("Failed to save cart to localStorage", e);
      }
    }
  }, [items]);

  const addToCart = (product, quantity = 1) => {
    setItems((prev) => {
      const existing = prev.find(
        (p) => p.variantId === product.variantId && p.id === product.id,
      );
      if (existing) {
        return prev.map((p) =>
          p.variantId === product.variantId && p.id === product.id
            ? { ...p, quantity: p.quantity + quantity }
            : p,
        );
      }
      return [...prev, { ...product, quantity }];
    });
  };

  const removeFromCart = (id, variantId) => {
    setItems((prev) =>
      prev.filter((item) =>
        variantId ? item.variantId !== variantId : item.id !== id,
      ),
    );
  };

  const clearCart = () => setItems([]);

  const cartCount = items.reduce((sum, item) => sum + (item.quantity || 0), 0);
  const cartTotal = items.reduce(
    (sum, item) => sum + (item.price || 0) * (item.quantity || 0),
    0,
  );

  const value = {
    items,
    addToCart,
    removeFromCart,
    clearCart,
    cartCount,
    cartTotal,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

function Header({ onToggleCart }) {
  const { cartCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const navLinkClass = ({ isActive }) =>
    `text-xs tracking-[0.2em] uppercase transition-colors duration-150 ${
      isActive ? "text-black" : "text-neutral-500 hover:text-black"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-neutral-200">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 md:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex flex-col leading-none">
            <span className="text-[11px] tracking-[0.35em] uppercase text-neutral-500">
              {SITE_META.brandName}
            </span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-400">
              Lash Education & Supplies
            </span>
          </Link>
        </div>

        <nav className="hidden items-center gap-8 md:flex">
          <NavLink to="/shop" className={navLinkClass}>
            Shop
          </NavLink>
          <NavLink to="/training" className={navLinkClass}>
            Training
          </NavLink>
          <NavLink to="/training-enquiry" className={navLinkClass}>
            Training Enquiry
          </NavLink>
          <NavLink to="/bookings" className={navLinkClass}>
            Bookings
          </NavLink>
          <NavLink to="/about" className={navLinkClass}>
            About
          </NavLink>
          <NavLink to="/gallery" className={navLinkClass}>
            Gallery
          </NavLink>
          <NavLink to="/reviews" className={navLinkClass}>
            Reviews
          </NavLink>
          <NavLink to="/faq" className={navLinkClass}>
            FAQ & Policies
          </NavLink>
          <NavLink to="/contact" className={navLinkClass}>
            Contact
          </NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <button
            type="button"
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 transition-colors duration-150 hover:border-neutral-900 hover:text-neutral-900 md:inline-flex"
            aria-label="Search"
          >
            <Search className="h-4 w-4" />
          </button>

          <button
            type="button"
            className="hidden h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 transition-colors duration-150 hover:border-neutral-900 hover:text-neutral-900 md:inline-flex"
            aria-label="Account"
          >
            <User className="h-4 w-4" />
          </button>

          <button
            type="button"
            onClick={onToggleCart}
            className="relative flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-800 transition-colors duration-150 hover:border-neutral-900"
            aria-label="Cart"
          >
            <ShoppingBag className="h-4 w-4" />
            {cartCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-black px-0.5 text-[10px] font-medium text-white">
                {cartCount}
              </span>
            )}
          </button>

          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-700 md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-t border-neutral-200 bg-white md:hidden">
          <nav className="mx-auto flex max-w-6xl flex-col gap-3 px-4 py-4 text-xs">
            <NavLink to="/shop" className={navLinkClass}>
              Shop
            </NavLink>
            <NavLink to="/training" className={navLinkClass}>
              Training
            </NavLink>
            <NavLink to="/training-enquiry" className={navLinkClass}>
              Training Enquiry
            </NavLink>
            <NavLink to="/bookings" className={navLinkClass}>
              Bookings
            </NavLink>
            <NavLink to="/about" className={navLinkClass}>
              About
            </NavLink>
            <NavLink to="/gallery" className={navLinkClass}>
              Gallery
            </NavLink>
            <NavLink to="/reviews" className={navLinkClass}>
              Reviews
            </NavLink>
            <NavLink to="/faq" className={navLinkClass}>
              FAQ & Policies
            </NavLink>
            <NavLink to="/contact" className={navLinkClass}>
              Contact
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}

function CartDrawer({ open, onClose }) {
  const { items, removeFromCart, clearCart, cartTotal } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!items.length) return;
    onClose();
    navigate("/checkout");
  };

  return (
    <div
      className={`fixed inset-y-0 right-0 z-50 w-full max-w-md transform border-l border-neutral-200 bg-white shadow-xl transition-transform duration-200 ease-out ${
        open ? "translate-x-0" : "translate-x-full"
      }`}
      aria-hidden={!open}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-neutral-200 px-5 py-4">
          <h2 className="text-sm font-medium tracking-[0.2em] uppercase text-neutral-700">
            Cart
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-neutral-200 text-neutral-600 hover:border-neutral-900 hover:text-neutral-900"
            aria-label="Close cart"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <p className="text-sm text-neutral-500">
              Your cart is currently empty. Start by adding your favorite lash
              essentials.
            </p>
          ) : (
            <ul className="space-y-4 text-sm">
              {items.map((item) => (
                <li
                  key={`${item.id}-${item.variantId}`}
                  className="flex items-start justify-between gap-3 border-b border-neutral-100 pb-4 last:border-b-0"
                >
                  <div>
                    <p className="font-medium text-neutral-900">{item.name}</p>
                    <p className="mt-1 text-xs text-neutral-500">
                      Qty {item.quantity} · €{item.price.toFixed(2)} each
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFromCart(item.id, item.variantId)}
                    className="text-xs uppercase tracking-[0.18em] text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-neutral-200 px-5 py-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-neutral-600">Subtotal</span>
            <span className="font-medium text-neutral-900">
              €{cartTotal.toFixed(2)}
            </span>
          </div>
          <p className="mt-2 text-xs text-neutral-500">
            Shipping calculated at checkout. Postal delivery across Ireland or
            free collection in Tralee.
          </p>
          <button
            type="button"
            data-testid="cart-checkout-btn"
            className="mt-4 flex h-10 w-full items-center justify-center rounded-full bg-black text-xs font-medium uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-neutral-800 disabled:cursor-not-allowed disabled:bg-neutral-300"
            disabled={items.length === 0}
            onClick={handleCheckout}
          >
            Proceed to checkout
          </button>
          {items.length > 0 && (
            <button
              type="button"
              onClick={clearCart}
              className="mt-3 w-full text-center text-[11px] uppercase tracking-[0.2em] text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline"
            >
              Clear cart
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="border-t border-neutral-200 bg-white">
      <div className="mx-auto flex max-w-6xl flex-col gap-8 px-4 py-10 text-xs text-neutral-600 md:flex-row md:justify-between md:px-6 lg:px-8">
        <div className="max-w-sm space-y-3">
          <p className="text-[11px] tracking-[0.3em] uppercase text-neutral-500">
            {SITE_META.brandName}
          </p>
          <p className="text-neutral-600">
            Lash education, pro lash products, and studio-quality tools for
            artists in Tralee and beyond.
          </p>
          <p className="text-neutral-400">
            Shop online with secure card checkout. Postal delivery across
            Ireland or free collection in Tralee.
          </p>
          <div className="pt-2 text-neutral-500">
            <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500">
              Studio address
            </p>
            <p className="mt-1 not-italic text-neutral-600">
              Milk Market Lane, Tralee,
              <br />
              County Kerry, V92 F3XY, Ireland
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Milk+Market+Lane,+Tralee,+County+Kerry,+V92+F3XY,+Ireland"
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-block text-neutral-500 underline-offset-4 hover:text-neutral-900 hover:underline"
            >
              Get directions
            </a>
          </div>
        </div>

        <div className="grid flex-1 grid-cols-2 gap-6 md:grid-cols-3">
          <div className="space-y-2">
            <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500">
              Shop
            </p>
            <Link to="/shop" className="block hover:text-neutral-900">
              Lash essentials
            </Link>
            <Link to="/training-enquiry" className="block hover:text-neutral-900">
              Training enquiry
            </Link>
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500">
              Studio
            </p>
            <Link to="/bookings" className="block hover:text-neutral-900">
              Book an appointment
            </Link>
            <Link to="/about" className="block hover:text-neutral-900">
              About Lash Dollz Vk Beauty
            </Link>
            <Link to="/gallery" className="block hover:text-neutral-900">
              Gallery
            </Link>
            <Link to="/reviews" className="block hover:text-neutral-900">
              Reviews
            </Link>
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-neutral-500">
              Support
            </p>
            <Link to="/faq" className="block hover:text-neutral-900">
              FAQ & policies
            </Link>
            <Link to="/contact" className="block hover:text-neutral-900">
              Contact
            </Link>
          </div>
        </div>
      </div>
      <div className="border-t border-neutral-100 py-4 text-center text-[11px] text-neutral-400">
        © {new Date().getFullYear()} {SITE_META.brandName}. All rights reserved.
      </div>
    </footer>
  );
}

export function LandingLayout({ children }) {
  const [cartOpen, setCartOpen] = useState(false);

  return (
    <CartProvider>
      <div className="flex min-h-screen flex-col bg-[#f7f4f1] text-neutral-900">
        <Header onToggleCart={() => setCartOpen(true)} />
        <main className="flex-1">
          <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 lg:px-8">
            {children}
          </div>
        </main>
        <Footer />
        <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
      </div>
    </CartProvider>
  );
}
